import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { CurrencyDollar, ArrowLeft, Lock, Download, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_TONE = {
  sent:    { label: "Awaiting payment", color: "#FFD43B" },
  partial: { label: "Partially paid",   color: "#FFA94D" },
  paid:    { label: "Paid in full",     color: "#28C878" },
  void:    { label: "Voided",           color: "#666" },
};

export default function ClientInvoice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invoices/${id}`);
        setInv(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invoice not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/checkout/session", {
        type: "invoice_payment",
        reference_id: id,
        origin_url: window.location.origin,
      });
      if (data.free) {
        navigate(`/payment/success?session_id=${data.session_id}`);
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setPaying(false);
    }
  };

  if (loading) {
    return <ClientLayout><div className="text-[#a3a3a3]">Loading…</div></ClientLayout>;
  }
  if (!inv) {
    return (
      <ClientLayout>
        <div className="border border-[#262626] bg-[#0a0a0a] p-8 text-center" data-testid="invoice-not-found">
          <h1 className="font-display text-3xl">Invoice not found</h1>
          <p className="text-[#a3a3a3] mt-2">It may have been voided or doesn&apos;t belong to your account.</p>
          <Link to="/dashboard" className="btn-outline mt-6 inline-flex px-5 py-3 text-sm uppercase tracking-wider">Back to dashboard</Link>
        </div>
      </ClientLayout>
    );
  }

  const tone = STATUS_TONE[inv.status] || STATUS_TONE.sent;
  const total = Number(inv.total_usd || 0);
  const paid = Number(inv.paid_amount || 0);
  const balance = Math.max(0, total - paid);
  const hasRecurring = (inv.line_items || []).some((l) => l.recurring);

  return (
    <ClientLayout>
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#a3a3a3] hover:text-white mb-6" data-testid="invoice-back">
        <ArrowLeft size={16} /> Dashboard
      </Link>

      <div className="max-w-2xl mx-auto" data-testid="client-invoice-page">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Invoice {inv.invoice_number}</div>
        <h1 className="font-display text-5xl mt-2">{inv.title}</h1>
        {inv.client_company && <div className="text-sm text-[#a3a3a3] mt-1">Billed to {inv.client_company}</div>}

        <div className="mt-8 border border-[#FF2D2D] bg-gradient-to-b from-[#FF2D2D]/10 to-transparent p-6">
          <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: tone.color }}>{tone.label}</div>
          <div className="font-display text-6xl mt-2 text-white tabular-nums" data-testid="invoice-balance">
            {formatUSD(balance > 0 ? balance : total)}
          </div>
          <div className="text-[11px] text-[#a3a3a3] mt-1 uppercase tracking-wider">
            {balance > 0 ? `Due ${inv.due_date?.slice(0,10)}` : "Settled"}
          </div>

          <div className="border-t border-[#262626] mt-5 pt-5 space-y-2" data-testid="invoice-line-items">
            {(inv.line_items || []).map((l) => (
              <div key={l.id} className="flex justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <div className="text-white truncate">{l.description}</div>
                  <div className="text-[11px] text-[#a3a3a3] flex items-center gap-1.5">
                    {l.quantity}× {formatUSD(l.unit_price)}
                    {l.recurring && (
                      <span className="text-[#FF2D2D] inline-flex items-center gap-1">
                        <ArrowsClockwise size={9} weight="bold" /> {l.recurring_interval}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-sm tabular-nums shrink-0">{formatUSD(l.line_total)}</div>
              </div>
            ))}
          </div>

          <div className="border-t border-[#262626] mt-5 pt-3 space-y-1 text-sm">
            <SummaryRow label="Subtotal" value={formatUSD(inv.subtotal_usd)} />
            {Number(inv.tax_usd) > 0 && <SummaryRow label={`Tax (${Number(inv.tax_pct).toFixed(2)}%)`} value={formatUSD(inv.tax_usd)} />}
            {paid > 0 && <SummaryRow label="Paid" value={`− ${formatUSD(paid)}`} />}
            <SummaryRow label={balance > 0 ? "Balance due" : "Total"} value={formatUSD(balance > 0 ? balance : total)} accent />
          </div>

          {inv.notes && (
            <div className="border-t border-[#262626] mt-5 pt-4 text-xs text-[#a3a3a3] whitespace-pre-wrap" data-testid="invoice-notes">
              {inv.notes}
            </div>
          )}
        </div>

        {balance > 0 && inv.status !== "void" && (
          <>
            {Number(inv.card_fee_pct || 0) > 0 && (
              <div className="mt-6 border border-[#262626] bg-[#0a0a0a] p-4 space-y-1 text-sm" data-testid="card-fee-breakdown">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] mb-2">Card checkout total</div>
                <SummaryRow label="Invoice balance" value={formatUSD(balance)} />
                <SummaryRow label={`Card processing fee (${Number(inv.card_fee_pct).toFixed(1)}%)`} value={`+ ${formatUSD(inv.card_fee_usd || 0)}`} />
                <div className="pt-2 border-t border-[#262626]">
                  <SummaryRow label="Charged to card" value={formatUSD(inv.card_total_usd || balance)} accent />
                </div>
                <div className="text-[10px] text-[#666] mt-1">
                  Prefer cash / Zelle / CashApp? Contact us to skip the {Number(inv.card_fee_pct).toFixed(1)}% surcharge.
                </div>
              </div>
            )}
            <button onClick={pay} disabled={paying} data-testid="pay-invoice-btn"
              className="btn-primary w-full mt-4 px-6 py-4 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50">
              <CurrencyDollar size={16} weight="bold" />
              {paying ? "Redirecting…" : `Pay ${formatUSD(inv.card_total_usd || balance)} now`}
            </button>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${id}/pdf`} target="_blank" rel="noreferrer"
              data-testid="download-invoice-pdf"
              className="mt-3 text-xs uppercase tracking-wider text-[#a3a3a3] hover:text-[#FF2D2D] flex items-center justify-center gap-1.5">
              <Download size={11} /> Download invoice PDF
            </a>
            {hasRecurring && (
              <div className="mt-4 border border-[#FF2D2D]/40 bg-[#FF2D2D]/5 p-3 text-xs text-[#FF2D2D] flex items-center gap-2" data-testid="recurring-notice">
                <ArrowsClockwise size={12} weight="bold" />
                This invoice has recurring lines. Next invoice will be issued automatically.
              </div>
            )}
          </>
        )}

        {balance <= 0 && inv.status === "paid" && (
          <div className="mt-6 border border-[#28C878]/40 bg-[#28C878]/5 p-4 text-sm text-[#28C878] flex items-center justify-between gap-3 flex-wrap" data-testid="invoice-paid-banner">
            <div className="flex items-center gap-2">
              <Lock size={14} weight="fill" /> This invoice has been paid in full. Thank you.
            </div>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${id}/pdf`} target="_blank" rel="noreferrer"
              data-testid="download-invoice-receipt"
              className="text-xs uppercase tracking-wider px-3 py-1.5 border border-[#28C878]/40 text-[#28C878] hover:bg-[#28C878]/10 flex items-center gap-1.5">
              <Download size={11} /> Receipt PDF
            </a>
          </div>
        )}
      </div>
    </ClientLayout>
  );
}

function SummaryRow({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between">
      <div className={`text-[10px] uppercase tracking-[0.3em] ${accent ? "text-[#FF2D2D]" : "text-[#a3a3a3]"}`}>{label}</div>
      <div className={`tabular-nums ${accent ? "font-display text-xl text-white" : "text-[#a3a3a3] text-sm"}`}>{value}</div>
    </div>
  );
}
